import { registerLocaleData } from '@angular/common';
import { provideHttpClient } from '@angular/common/http';
import localeEsCl from '@angular/common/locales/es-CL';
import { ApplicationConfig, ErrorHandler, inject, isDevMode, provideAppInitializer } from '@angular/core';
import { LuxonDateAdapter } from '@angular/material-luxon-adapter';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { Title } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { provideMotoBoy } from '@fuse';
import { FuseConfigService } from '@fuse/services/config';
import { TranslocoService, provideTransloco } from '@jsverse/transloco';
import { appRoutes } from 'app/app.routes.admin';
import { BrandService } from 'app/brand/brand.service';
import { provideAuth } from 'app/core/auth/auth.provider';
import { provideIcons } from 'app/core/icons/icons.provider';
import { VersionCheckService } from 'app/core/version/version-check.service';
import { GlobalErrorHandlerService } from 'app/global-error-handler.service';
import { firstValueFrom } from 'rxjs';
import { TranslocoHttpLoader } from './core/transloco/transloco.http-loader';

registerLocaleData(localeEsCl, 'es-CL');

type ColorScheme = 'light' | 'dark';
/**
 * Obtiene el scheme inicial desde localStorage con fallback seguro.
 * Forzamos el tipo para evitar valores no válidos.
 */
function getInitialScheme(): ColorScheme {
    try {
        const raw = typeof window !== 'undefined' ? localStorage.getItem('scheme') : null;
        if (raw === 'light' || raw === 'dark') return raw;
    } catch {
        // Ignorar errores de acceso a localStorage (p.ej., SSR o privacidad)
    }
    return 'dark'; // valor por defecto
}

export const appConfig: ApplicationConfig = {
    providers: [
        provideAnimations(),
        provideHttpClient(),
        { provide: ErrorHandler, useClass: GlobalErrorHandlerService },
        provideRouter(appRoutes, withInMemoryScrolling({ scrollPositionRestoration: 'enabled' })),

        // Material Date Adapter
        {
            provide: DateAdapter,
            useClass: LuxonDateAdapter,
        },
        {
            provide: MAT_DATE_FORMATS,
            useValue: {
                parse: {
                    dateInput: 'D',
                },
                display: {
                    dateInput: 'DDD',
                    monthYearLabel: 'LLL yyyy',
                    dateA11yLabel: 'DD',
                    monthYearA11yLabel: 'LLLL yyyy',
                },
            },
        },

        // Transloco Config
        provideTransloco({
            config: {
                availableLangs: [
                    {
                        id: 'en',
                        label: 'English',
                    },
                    {
                        id: 'es',
                        label: 'Español',
                    },
                ],
                defaultLang: 'es',
                fallbackLang: 'es',
                reRenderOnLangChange: true,
                prodMode: !isDevMode(),
            },
            loader: TranslocoHttpLoader,
        }),
        provideAppInitializer(() => {
            const translocoService = inject(TranslocoService);
            const savedLang = localStorage.getItem('transloco-lang');
            const langToUse = savedLang || translocoService.getDefaultLang();
            translocoService.setActiveLang(langToUse);

            return firstValueFrom(translocoService.load(langToUse));
        }),
        // Branding por dominio: si el host es *.muve.* (u override), aplica el theme MUVE
        // y el título correspondiente. Respeta el toggle claro/oscuro del usuario (solo cambia paleta).
        provideAppInitializer(() => {
            const brand = inject(BrandService);
            const tokens = brand.tokens();
            if (brand.isMuve()) {
                inject(FuseConfigService).config = { theme: tokens.theme };
                // Favicon MUVE (backstop del script inline de index.html). Solo bajo brand MUVE
                // para no alterar el favicon MotoBoy.
                try {
                    document.head
                        .querySelectorAll('link[rel~="icon"], link[rel="shortcut icon"], link[rel="apple-touch-icon"]')
                        .forEach((l) => l.remove());
                    const link = document.createElement('link');
                    link.rel = 'icon';
                    link.type = 'image/svg+xml';
                    link.href = tokens.favicon;
                    document.head.appendChild(link);
                } catch {
                    /* noop */
                }
            }
            inject(Title).setTitle(tokens.title);
        }),
        // Pestañas abiertas antes de un deploy: recargar al navegar si hay build nuevo
        provideAppInitializer(() => {
            inject(VersionCheckService).start();
        }),

        // MotoBoy
        provideAuth(),
        provideIcons(),
        provideMotoBoy({
            fuse: {
                layout: 'classy',
                scheme: getInitialScheme(),
                screens: {
                    sm: '600px',
                    md: '960px',
                    lg: '1280px',
                    xl: '1440px',
                },
                theme: 'theme-amber',
                themes: [
                    {
                        id: 'theme-amber',
                        name: 'Amber',
                    },
                    {
                        id: 'theme-muve',
                        name: 'MUVE',
                    },
                ],
            },
        }),
    ],
};
